import React, { useState, useEffect } from 'react';
import { 
  Typography, Container, Paper, Button, Box, Dialog, DialogTitle, 
  DialogContent, DialogActions, TextField, MenuItem, IconButton, Tooltip 
} from '@mui/material';
import { Add as AddIcon, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { getAllHolidays, createHoliday } from '../../api/holidayAPI';
import moment from 'moment';

const Holidays = () => {
  const { user } = useSelector((state) => state.tokens);
  const isAdmin = user?.permission === 'Admin' || user?.permission === 'HR Management';
  
  const [holidays, setHolidays] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', date: '', description: '', type: 'Mandatory' });
  
  const [currentDate, setCurrentDate] = useState(moment());

  const fetchHolidays = async () => {
    try {
      const res = await getAllHolidays();
      if(res.data.success) {
        setHolidays(res.data.holidays);
      }
    } catch(err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      await createHoliday(formData);
      setOpenForm(false);
      setFormData({ name: '', date: '', description: '', type: 'Mandatory' });
      fetchHolidays();
    } catch(err) {
      console.error(err);
    }
  };
  
  // Calendar Rendering Logic
  const startDay = currentDate.clone().startOf('month').startOf('week');
  const endDay = currentDate.clone().endOf('month').endOf('week');
  const day = startDay.clone().subtract(1, 'day');
  const calendar = [];
  while (day.isBefore(endDay, 'day')) {
    calendar.push(
      Array(7).fill(0).map(() => day.add(1, 'day').clone())
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" fontWeight="bold">Company Holidays</Typography>
        {isAdmin && (
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setOpenForm(true)}>
            Add Holiday
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => setCurrentDate(currentDate.clone().subtract(1, 'month'))}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="h5" fontWeight="medium">
            {currentDate.format('MMMM YYYY')}
          </Typography>
          <IconButton onClick={() => setCurrentDate(currentDate.clone().add(1, 'month'))}>
            <ChevronRight />
          </IconButton>
        </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <Box key={d} sx={{ textAlign: 'center', fontWeight: 'bold', pb: 1, color: d === 'Sun' ? 'error.main' : 'inherit' }}>
                  {d}
                </Box>
              ))}
              {calendar.flat().map((dayItem, index) => {
                const dateStr = dayItem.format('YYYY-MM-DD');
                const isCurrentMonth = dayItem.month() === currentDate.month();
                const isSunday = dayItem.day() === 0;
                const dayHolidays = holidays.filter(h => moment(h.date).format('YYYY-MM-DD') === dateStr);
                
                return (
                  <Box 
                    key={index}
                    sx={{
                      minHeight: 100,
                      p: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      bgcolor: isSunday ? '#ffebee' : (isCurrentMonth ? '#fff' : '#f5f5f5'),
                      color: isCurrentMonth ? 'text.primary' : 'text.disabled',
                      transition: '0.2s',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        bgcolor: isSunday ? '#ffcdd2' : '#f0f4f8'
                      }
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      fontWeight={dayItem.isSame(moment(), 'day') ? 'bold' : 'normal'} 
                      color={dayItem.isSame(moment(), 'day') ? 'primary' : (isSunday && isCurrentMonth ? 'error.main' : 'inherit')}
                    >
                      {dayItem.date()}
                    </Typography>

                    {/* Render Holiday Label for Sunday */}
                    {isSunday && isCurrentMonth && (
                      <Box sx={{
                        position: 'absolute', top: 5, right: 5, 
                        fontSize: '0.5rem', fontWeight: 'bold', color: 'error.main', opacity: 0.7
                      }}>
                        COMPANY HOLIDAY
                      </Box>
                    )}
                {dayHolidays.map((h) => (
                  <Tooltip title={h.description || ''} key={h._id}>
                    <Box sx={{
                      mt: 1,
                      p: 0.5,
                      bgcolor: h.type === 'Mandatory' ? 'error.main' : 'warning.main',
                      color: '#fff',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      lineHeight: '1.2',
                      textAlign: 'center',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {h.name}
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            );
          })}
        </Box>
      </Paper>

      {/* Create Modal */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Holiday</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label="Holiday Name"
            name="name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Date"
            name="date"
            type="date"
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={formData.date}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            name="description"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={formData.description}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            margin="dense"
            label="Type"
            name="type"
            fullWidth
            variant="outlined"
            value={formData.type}
            onChange={handleChange}
          >
            <MenuItem value="Mandatory">Mandatory</MenuItem>
            <MenuItem value="Optional">Optional</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.name || !formData.date}>Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Holidays;
